import React, { useEffect, useState } from 'react';
import { Badge, Button, Card, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import positionService from '@/shared/services/positionService';
import { positionPath } from '@/features/positions/positionLinks';
import { badgeTextFor } from '@/shared/utils/badgeText';

export const STAFFING_WORDS = {
    la: { one: 'LA', many: 'LAs' },
    si: { one: 'SI Leader', many: 'SI Leaders' },
};

const NOT_FOUND = 404;

// An LA coordinator or admin who is not faculty on the request cannot open
// My LA positions (faculty only), so their link goes to the Positions view of
// their own LA dashboard, open on this section
const coordinatorPath = (applicationId) =>
    `/programs/la?tab=positions&open=${encodeURIComponent(applicationId)}`;

/**
 * How many places the program awarded a section and who holds them, from the
 * staffing read. The read answers 404 to anyone who may not see the award, so
 * a 404 renders nothing rather than an error. Only the counts and the names
 * are read from the response. `isFaculty` is true when the viewer is the
 * applicant or the instructor of record on the request.
 */
export default function StaffingPanel({
    applicationId,
    program,
    headingLevel = 2,
    isFaculty = false,
}) {
    const [staffing, setStaffing] = useState(null);
    const [state, setState] = useState('loading');
    const [attempt, setAttempt] = useState(0);
    const words = STAFFING_WORDS[program] || STAFFING_WORDS.la;

    useEffect(() => {
        let current = true;
        setState('loading');
        positionService
            .getStaffing(applicationId, program)
            .then((data) => {
                if (!current) return;
                setStaffing(data);
                setState('ready');
            })
            .catch((err) => {
                if (!current) return;
                setState(
                    err?.response?.status === NOT_FOUND ? 'hidden' : 'error'
                );
            });
        return () => {
            current = false;
        };
    }, [applicationId, program, attempt]);

    if (state === 'hidden') return null;

    const Heading = `h${headingLevel}`;
    const SubHeading = `h${headingLevel + 1}`;

    if (state === 'loading') {
        return (
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body role="status">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading staffing...
                </Card.Body>
            </Card>
        );
    }

    if (state === 'error') {
        return (
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Heading className="h5 mb-3">{words.one} staffing</Heading>
                    <p role="alert" className="mb-2">
                        The staffing for this section couldn&apos;t be loaded.
                    </p>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setAttempt((n) => n + 1)}
                    >
                        Retry
                    </Button>
                </Card.Body>
            </Card>
        );
    }

    const { position, holders } = staffing;
    const offered =
        (position.offersOutstanding || 0) + (position.nomineeHeld || 0);
    const closed = position.closedByDecline || 0;

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    <Heading className="h5 mb-0">{words.one} staffing</Heading>
                    {position.fullyStaffed && (
                        <Badge bg="success" text={badgeTextFor('success')}>
                            Fully staffed
                        </Badge>
                    )}
                </div>
                <p>
                    {position.awarded} of {position.requested} {words.many}{' '}
                    awarded: {position.filled} accepted, {offered} offered,{' '}
                    {position.openSlots} open
                    {closed > 0 && `, ${closed} closed when a nominee declined`}
                    .
                </p>
                {program === 'la' && (
                    <Link
                        to={
                            isFaculty
                                ? positionPath(applicationId)
                                : coordinatorPath(applicationId)
                        }
                        className="btn btn-outline-primary btn-sm mb-3"
                    >
                        Review candidates
                        <span className="visually-hidden">
                            {' '}
                            {isFaculty
                                ? 'in My LA positions'
                                : 'in LA Positions'}
                        </span>
                    </Link>
                )}
                <SubHeading className="h6">
                    {words.many} in this section
                </SubHeading>
                {holders.length === 0 ? (
                    <p className="text-muted mb-0">
                        No {words.one} has accepted yet.
                    </p>
                ) : (
                    <ul className="mb-0">
                        {holders.map((holder, index) => (
                            <li key={index}>
                                {holder.name || 'Name unavailable'}
                            </li>
                        ))}
                    </ul>
                )}
            </Card.Body>
        </Card>
    );
}
